/**
 * @file TableExportModal.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-04
 * @modified 2026-11-02
 * @description Turn the table on screen into a PDF, then save it, print it or
 *              email it.
 *
 *              The payload is built by the caller at CLICK time, not on every
 *              render, and not here — a screen with 200 rows should not be
 *              formatting all of them into strings on the chance somebody
 *              presses the button, and only the screen knows what it is
 *              currently showing: its filters, its visible columns, its
 *              grouping, its subtotals. `build()` is called exactly once, when
 *              the modal opens, so what is sent is what was on screen at the
 *              moment the button was pressed — not what the screen drifted to
 *              while the user was choosing an orientation.
 *
 *              Follows BlendExportModal's three-mode card layout and reuses its
 *              CSS, so every export in the system looks and behaves the same —
 *              including "What to do with it" in the footer (ExportModeFooter).
 *
 *              What goes in (rows, columns, totals, line details) is chosen in
 *              ExportContentOptions and applied to the built payload by
 *              shapeExportPayload; the preview and the PDF are both given the
 *              shaped result. Only the ROWS choice rebuilds: "all results"
 *              needs the screen to fetch past its current page, so build() is
 *              called again with { scope } — the other choices trim what is
 *              already there.
 *
 *              A report too big to post from the screen (Reports → Invoice
 *              Detail, 1.3.0 — a month is thousands of lines) is built on the
 *              server instead: the caller passes its own `endpoint` /
 *              `previewEndpoint` and `requestFor(shaped, { hiddenColumns,
 *              hiddenTotals })`, which turns the shaped payload into the request
 *              those take (what to read, which columns and totals), and its
 *              payload says `bodyRowCount` / `groupCount` / `totalKeys` for the
 *              rows it does not carry. Every other screen is unchanged.
 *
 *              The From address defaults to the acting user's own — the token
 *              'me', which the server resolves — and SenderSelect offers the
 *              shared mailboxes as the alternative. The client never sends a
 *              raw address, so the sender is always inside the allowlist.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import ExportPreview from './ExportPreview';
import ExportLayoutOptions from './ExportLayoutOptions';
import ExportContentOptions, { shapeExportPayload } from './ExportContentOptions';
import Input from './Input';
import Textarea from './Textarea';
import { defaultSender } from './SenderSelect';
import ExportModeFooter, { revealOnMount } from './ExportModeFooter';
import { isReadOnly } from '../utils/permissions';
import './Formulations.css';
import './TableExportModal.css';

const MODES = [
  { key: 'download', icon: 'download', label: 'Save', hint: 'Download the PDF to this device' },
  { key: 'print', icon: 'print', label: 'Print', hint: 'Send straight to the office printer' },
  { key: 'email', icon: 'mail', label: 'Email', hint: 'Attach it to an email' }
];

/**
 * The options column. The slide-out buttons are in the footer, and
 * ExportModeFooter measures them; nothing here needs useSlideOutButton.
 */
function ExportBody({
  summary, mode, layout, setLayoutField, previewPayload, previewEndpoint = '/api/exports/table/preview',
  user, sendAs, setSendAs, to, setTo, subject, setSubject, message, setMessage,
  error, blocked, content
}) {
  return (
    <div className="table-export-split">
      <div className="table-export-body">
      {error && <div className="formulation-error">{error}</div>}
      {blocked && (
        <div className="table-export-warning">
          <span className="material-symbols-rounded">warning</span>
          <span>{blocked}</span>
        </div>
      )}

      {summary && <p className="fm-export-summary">{summary}</p>}

      <ExportContentOptions {...content} />

      <ExportLayoutOptions
        orientation={layout.orientation}
        onOrientation={(v) => setLayoutField('orientation', v)}
        fit={layout.fit}
        onFit={(v) => setLayoutField('fit', v)}
        scale={layout.scale}
        onScale={(v) => setLayoutField('scale', v)}
      />

      {/* The mode is picked in the footer; what it needs stays here. */}
      {mode === 'email' && (
        <div className="fm-export-email" ref={revealOnMount}>
          <p className="table-export-note">
            Sent to you{user?.email ? ` — ${user.email}` : ''}. An export goes to your own inbox only.
          </p>
          <div className="fm-export-field">
            <label htmlFor="tx-subject">Subject</label>
            <Input id="tx-subject" type="text" value={subject}
                   placeholder="Default subject will be used if blank"
                   onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="fm-export-field">
            <label htmlFor="tx-message">Message</label>
            <Textarea id="tx-message" rows={3} value={message}
                      placeholder="Optional covering message…"
                      onChange={(e) => setMessage(e.target.value)} />
          </div>
        </div>
      )}

      {mode === 'print' && (
        <p className="table-export-note">
          Goes to your configured printer queue. The printer is checked before
          anything is rendered, so an offline print bridge says so rather than
          swallowing the job.
        </p>
      )}
      </div>

      {/* The document itself, rendered from the same html the PDF is built
          from — see ExportPreview. */}
      <ExportPreview
        endpoint={previewEndpoint}
        payload={previewPayload}
        disabled={!!blocked}
        note={blocked ? 'Nothing to preview' : ''}
      />
    </div>
  );
}

export default function TableExportModal({
  build,
  endpoint = '/api/exports/table',
  previewEndpoint = '/api/exports/table/preview',
  requestFor = null,
  title = 'Export',
  modalTitle = 'Export this table',
  summary = '',
  user,
  defaultOrientation = 'portrait',
  onClose,
  onDone
}) {
  const [mode, setMode] = useState('download');
  /*
   * Orientation now travels with the rest of the page layout. It keeps its own
   * setter so the caller's defaultOrientation still decides how the modal opens.
   */
  // Portrait, fitted to the page width, unless the screen says otherwise: a
  // wide table shrinks to fit rather than running off the side.
  const [layout, setLayout] = useState(
    { orientation: defaultOrientation, fit: 'width', scale: 1 });
  const setLayoutField = useCallback(
    (key, value) => setLayout((prev) => ({ ...prev, [key]: value })), []);
  const [sendAs, setSendAs] = useState(() => defaultSender());
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // The payload is snapshotted when the modal opens — before the user touches
  // the mode or orientation cards — so "what was on screen when I pressed the
  // button" stays true even if something behind the modal re-renders while they
  // are deciding.
  const [payload, setPayload] = useState(null);

  // Everything, by default — see ExportContentOptions.
  const [scope, setScope] = useState('all');
  const [scopeBusy, setScopeBusy] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState(() => new Set());
  const [hiddenTotals, setHiddenTotals] = useState(() => new Set());
  const [includeLines, setIncludeLines] = useState(true);

  // Built when the modal opens, and again only when the rows choice changes.
  // A screen that cannot fetch past its page simply ignores { scope } and
  // sends no `available`, and the choice is never offered. The previous
  // payload stays up while a rebuild runs, so the preview does not blank.
  useEffect(() => {
    let alive = true;
    setScopeBusy(true);
    Promise.resolve()
      .then(() => build({ scope }))
      .then((p) => { if (alive) setPayload(p || null); })
      .catch((e) => { if (alive) setError(e.message || 'Could not build the export'); })
      .finally(() => { if (alive) setScopeBusy(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const toggleIn = (setter) => (key) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const shaped = useMemo(
    () => shapeExportPayload(payload, { hiddenColumns, hiddenTotals, includeLines }),
    [payload, hiddenColumns, hiddenTotals, includeLines]);

  const bodyRows = useMemo(() => {
    if (!shaped) return 0;
    // A server-built export says how many rows it will carry.
    if (Number.isFinite(shaped.bodyRowCount)) return shaped.bodyRowCount;
    return (shaped.rows?.length || 0)
      + (shaped.groups || []).reduce((sum, g) => sum + (g.rows?.length || 0), 0);
  }, [shaped]);

  const blocked = useMemo(() => {
    if (!shaped) return null;
    if (!bodyRows) return 'There is nothing on screen to export. Run the report first.';
    return null;
  }, [shaped, bodyRows]);

  /*
   * The document without how it is delivered — the same object the export
   * posts, so the preview cannot be of something else. A server-built export
   * posts its request instead (requestFor).
   */
  const requestBody = useMemo(
    () => (shaped && requestFor ? requestFor(shaped, { hiddenColumns, hiddenTotals }) : shaped),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shaped, hiddenColumns, hiddenTotals]);
  const previewPayload = useMemo(
    () => (requestBody ? { ...requestBody, layout } : null), [requestBody, layout]);

  const submit = async () => {
    if (!shaped) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...requestBody,
          // Kept for the server's older callers; `layout` is what actually
          // decides, and layoutFrom() prefers it.
          landscape: layout.orientation === 'landscape',
          layout,
          mode,
          ...(mode === 'email' ? { subject, message } : {})
        })
      });

      if (mode === 'download') {
        if (!res.ok) {
          // The endpoint answers JSON on failure and a PDF on success, so a
          // failed response must never reach the download path — that saves an
          // error message as a .pdf nobody can open.
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Could not build the export');
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${String(payload.filename || title || 'export')
          .replace(/[^\w\d\-. ]+/g, '').trim().replace(/\s+/g, '-') || 'export'}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        // Revoked on the next tick: revoking synchronously can beat the browser
        // to the download in Safari and produce an empty file.
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        onDone?.('Downloaded');
        return;
      }

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not build the export');
      onDone?.(data.message);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // A view-only account may download an export of what it can already see, but
  // driving the office printer and sending mail from an @excel-agri.com address
  // are not that. Disabled rather than hidden, with the reason on the card —
  // the server refuses these independently.
  const viewOnly = isReadOnly(user);
  const modes = useMemo(() => MODES.map((m) => ((viewOnly && m.key !== 'download')
    ? { ...m, disabled: true, hint: 'Not available on a view-only account',
        title: 'View-only accounts can download an export but not print or email one' }
    : m)), [viewOnly]);

  const verb = mode === 'download' ? 'Save' : mode === 'print' ? 'Print' : 'Send';
  const icon = mode === 'download' ? 'download' : mode === 'print' ? 'print' : 'send';

  const groupTotal = shaped
    ? (Number.isFinite(shaped.groupCount) ? shaped.groupCount : (shaped.groups?.length || 0))
    : 0;
  const summaryLine = summary
    || (shaped
      ? `${bodyRows.toLocaleString('en-GB')} ${shaped.rowNoun || 'rows'}${groupTotal ? ` across ${groupTotal.toLocaleString('en-GB')} group${groupTotal !== 1 ? 's' : ''}` : ''}, `
        + `${shaped.columns?.length || 0} columns.`
      : 'Reading what is on screen…');

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={modalTitle}
      size="lg"
      className="formulation-export table-export"
      footer={
        <ExportModeFooter modes={modes} mode={mode} onMode={setMode} busy={busy}>
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={busy}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            {/* A modal footer defaults to DENY for a view-only account (see the
                data-readonly rules in App.css), and downloading an export is
                one of the few writes a REP is allowed — it is a carve-out in
                middleware/readOnly.js. So the action button opts out only while
                it is the download; print and email stay greyed for them, which
                is also what the server enforces. */}
            <button className="btn-slideout" onClick={submit}
                    data-read-safe={mode === 'download' ? '' : undefined}
                    disabled={busy || scopeBusy || !shaped || !!blocked}>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded${busy ? ' spinning' : ''}`}>
                  {busy ? 'progress_activity' : icon}
                </span>
              </div>
              <span className="btn-label">{busy ? 'Working…' : verb}</span>
            </button>
          </div>
        </ExportModeFooter>
      }
    >
      <ExportBody
        summary={summaryLine}
        mode={mode}
        layout={layout} setLayoutField={setLayoutField}
        previewPayload={previewPayload}
        previewEndpoint={previewEndpoint}
        user={user}
        sendAs={sendAs} setSendAs={setSendAs}
        to={to} setTo={setTo}
        subject={subject} setSubject={setSubject}
        message={message} setMessage={setMessage}
        error={error} blocked={blocked}
        content={{
          payload,
          scope, onScope: setScope, available: payload?.available, scopeBusy,
          hiddenColumns, onToggleColumn: toggleIn(setHiddenColumns),
          hiddenTotals, onToggleTotal: toggleIn(setHiddenTotals),
          includeLines, onIncludeLines: setIncludeLines,
        }}
      />
    </Modal>
  );
}
